import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {PageHeader} from "../../components/PageHeader";
import Section from "../../components/section/Section";
import {getDataByParentIdAndTableName} from "../../api/static/StaticService";
import {CodeSpecNum} from "../../constant/CodeSpecNum";
import {selectCodesByParentId} from "../../api/code/CodeService";
import {RecursiveList} from "../../components/recursivelist/RecursiveList";
import {INCIDENT_HEADER, INSPECTION_HEADER, WORK_HEADER} from "../../components/table/header/Headers";
import {getTopCompanylist} from "../../api/company/CompanyService";
import {simmsColumnsToJsGridHeaders, toKSTISOString, toKSTISOStringEnd} from "../../components/commonmethod/CommonMethod";
import {addMonths} from "date-fns";
import AssetTypeStatusChart from "../chart/AssetTypeStatusChart";
import StaticDateRangeFilter from "./StaticDateRangeFilter";
import {JsGrid} from "@bavuchoko/js-grid";
import {navigateWithChildTab, pickDetailTabTitle} from "../../components/pageTabs/pageTabsStorage";

function parseJsGridPageable(pageable, prev) {
    const pageNumber = pageable?.pageNumber ?? prev?.currentPage ?? 0;
    const size = pageable?.pageSize ?? pageable?.size ?? prev?.size ?? 10;
    let sort = prev?.sort ?? 'createdAt';
    let desc = prev?.desc ?? true;

    const sortArr = pageable?.sort;
    const token = Array.isArray(sortArr) && sortArr[0] != null ? String(sortArr[0]) : null;
    if (token) {
        const comma = token.indexOf(',');
        if (comma >= 0) {
            sort = token.slice(0, comma).trim();
            desc = token.slice(comma + 1).trim().toLowerCase() === 'desc';
        } else {
            sort = token.trim();
            if (pageable?.sortDirection != null) {
                desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
            }
        }
    } else if (pageable?.sortDirection != null) {
        desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
    }

    return {pageNumber, size, sort, desc};
}

function resolveTypeId(table, type) {
    switch (type) {
        case "group":
            return CodeSpecNum.AssetGroupCode;
        case "task":
            return CodeSpecNum.TaskGroupCode;
        case "company":
            return CodeSpecNum.CompanyParentKey;
        case `${table}-code`:
            if (table === "inspection") return CodeSpecNum.InspectionRequestCode;
            if (table === "incident") return CodeSpecNum.IncidentRequestCode;
            return CodeSpecNum.WorkRequestCode;
        default:
            return null;
    }
}

function StaticMaintenanceType() {
    const {table, type} = useParams();
    const navigate = useNavigate();
    const typeId = resolveTypeId(table, type);

    const [chart, setChart] = useState();
    const [list, setList] = useState([]);
    const [code, setCode] = useState();
    const [selected, setSelected] = useState();
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        page: 0,
        totalPages: 0,
        totalElements: 0,
        size: 10,
        sort: 'createdAt',
        desc: true,
    });
    const [date, setDate] = useState({
        startDate: toKSTISOString(addMonths(new Date(), -3)),
        endDate: toKSTISOStringEnd(new Date()),
    });

    const tableHeader = table === "inspection"
        ? INSPECTION_HEADER
        : table === "incident"
            ? INCIDENT_HEADER
            : WORK_HEADER;

    const jsGridHeader = useMemo(
        () => simmsColumnsToJsGridHeaders(tableHeader),
        [tableHeader]
    );

    const gridData = useMemo(() => ({
        content: list ?? [],
        pageable: {
            pageNumber: pageInfo.currentPage ?? pageInfo.page ?? 0,
            pageSize: pageInfo.size ?? 10,
            size: pageInfo.size ?? 10,
            sort: pageInfo.sort ? [`${pageInfo.sort},${pageInfo.desc ? 'desc' : 'asc'}`] : undefined,
            sortDirection: pageInfo.desc ? 'DESC' : 'ASC',
            totalElements: pageInfo.totalElements,
            totalPages: pageInfo.totalPages,
        },
        totalElements: pageInfo.totalElements,
        totalPages: pageInfo.totalPages,
    }), [list, pageInfo]);

    useEffect(() => {
        if (typeId == null) return;

        setSelected(typeId);

        if (type === "company") {
            getTopCompanylist().then((response) => {
                setCode(response.data.content);
            });
        } else {
            selectCodesByParentId(typeId).then((response) => {
                setCode(response);
            });
        }

        fetchList(typeId, 0);
    }, [table, type]);

    function applyListResponse(r, reqPageInfo) {
        setChart(r.data.data);
        setList(r.data.list.content ?? []);
        setPageInfo({
            page: r.data.list.pageable.pageNumber,
            currentPage: r.data.list.pageable.pageNumber,
            totalElements: r.data.list.totalElements,
            totalPages: r.data.list.totalPages,
            size: reqPageInfo.size ?? 10,
            sort: reqPageInfo.sort ?? 'createdAt',
            desc: reqPageInfo.desc ?? true,
        });
    }

    function fetchList(value, pageNumber = 0, overrides = {}) {
        const nextDate = overrides.date ?? date;
        const reqPageInfo = {
            ...pageInfo,
            ...overrides,
            page: pageNumber,
            currentPage: pageNumber,
        };

        getDataByParentIdAndTableName(
            value,
            reqPageInfo,
            table,
            type,
            nextDate.startDate,
            nextDate.endDate
        ).then((r) => applyListResponse(r, reqPageInfo));
    }

    function chaneTargetCodeHandler(value) {
        setSelected(value.id);
        fetchList(value.id, 0);
    }

    const title = {
        'group': '자산그룹별',
        'task': '업무그룹별',
        'company': '업체별',
        'incident-code': ' 유형별',
        'work-code': ' 종류별',
        'inspection-code': ' 종류별',
    };

    const header = {
        'inspection': '점검',
        'incident': '장애',
        'work': '작업',
    };

    const sidebarTitle = {
        'group': '자산그룹 목록',
        'task': '업무그룹 목록',
        'company': '업체 목록',
        'incident-code': '장애 유형 목록',
        'work-code': '작업 종류 목록',
        'inspection-code': '점검 종류 목록',
    };

    const detailFallback = {
        inspection: '점검 상세',
        incident: '장애 상세',
        work: '작업 상세',
    }[table] ?? '상세';

    const handleDateRangeChange = (newDate) => {
        setDate(newDate);
        fetchList(selected, 0, {date: newDate});
    };

    const handleGridPageChange = (pageable) => {
        const {pageNumber, size, sort, desc} = parseJsGridPageable(pageable, pageInfo);
        fetchList(selected, pageNumber, {size, sort, desc});
    };

    const handleRowClick = (row) => {
        const id = row?.id;
        if (id == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, detailFallback),
            url: `/maintenance/${table}/${id}`,
        });
    };

    const pageTitle = `${type.includes("code") ? header[table] : ""} ${title[type]} 현황`.trim();

    return (
        <div className="common-container container-height-full">
            <PageHeader
                header={pageTitle}
                indicator={`유지보수업무 > ${header[table]}관리 > ${pageTitle}`}
            >
                <StaticDateRangeFilter date={date} onChange={handleDateRangeChange}/>
            </PageHeader>

            <Section className="mb-none flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 flex-1 w-full gap-[8px]">
                    <aside className="flex w-[240px] shrink-0 flex-col overflow-hidden border rounded-si bg-white">
                        <div className="shrink-0 border-b border-[#eef2f7] px-3 pb-2 pt-3">
                            <span className="inline-block border-b-2 border-[#1c90fb] pb-1 text-[14px] font-semibold text-[#333]">
                                {sidebarTitle[type]}
                            </span>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-2">
                            {code &&
                                <RecursiveList data={code} setTarget={chaneTargetCodeHandler} readOnly/>
                            }
                        </div>
                    </aside>

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[8px]">
                        <div className="flex h-[min(280px,32vh)] min-h-[220px] shrink-0 flex-col overflow-hidden border rounded-si bg-white p-[16px]">
                            {chart &&
                                <AssetTypeStatusChart data={chart}/>
                            }
                        </div>

                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden border rounded-si bg-white">
                            <div className="container-height-body min-h-0 flex-1">
                                <JsGrid
                                    resizable={true}
                                    data={gridData}
                                    theme="linear"
                                    style={{minHeight: 0, height: '100%'}}
                                    header={jsGridHeader}
                                    onRowClick={handleRowClick}
                                    onPageChange={handleGridPageChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Section>
        </div>
    );
}

export default StaticMaintenanceType;
